/**
 * Belle Reviews Advanced - Interactive Review Slider System
 * Features: Slider navigation, rating filters, autoplay, pagination, load more
 */

class BelleReviewsAdvanced {
  constructor(section) {
    this.section = section;
    this.slider = section.querySelector('[data-reviews-slider]');
    this.slides = Array.from(section.querySelectorAll('.belle-review-slide'));
    this.filterButtons = Array.from(section.querySelectorAll('.belle-filter-btn'));
    this.loadMoreBtn = section.querySelector('[data-load-more]');
    this.dotsContainer = section.querySelector('[data-slider-dots]');

    this.currentIndex = 0;
    this.visibleSlides = this.slides;
    this.autoplayInterval = null;

    // Get settings from section attributes
    this.slidesToShow = parseInt(section.dataset.slidesToShow) || 3;
    this.autoplay = section.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(section.dataset.autoplaySpeed) || 5;
    this.initialReviews = parseInt(section.dataset.initialReviews) || 6;

    this.init();
  }

  init() {
    if (!this.slider) return;

    this.setupLoadMore(); // Setup load more first to set initial visibility
    this.setupFilters();
    this.setupSlider();
    this.updateVisibleSlides(); // Calculate initial visible slides
    this.setupDots();
    this.updateNavButtons();

    if (this.autoplay) {
      this.startAutoplay();
      this.setupAutoplayPause();
    }

    // Handle responsive behavior
    this.handleResize();
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  setupFilters() {
    if (!this.filterButtons.length) return;

    this.filterButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const filterValue = button.dataset.filter;
        this.filterReviews(filterValue);

        // Update active state
        this.filterButtons.forEach((btn) => btn.classList.remove('active'));
        button.classList.add('active');
      });
    });
  }

  filterReviews(rating) {
    this.slides.forEach((slide) => {
      const matchesFilter = rating === 'all' || slide.dataset.rating === rating;

      if (matchesFilter) {
        slide.classList.remove('hidden-by-filter');
      } else {
        slide.classList.add('hidden-by-filter');
      }
    });

    this.updateVisibleSlides();

    // Reset slider position
    this.currentIndex = 0;
    this.slider.scrollLeft = 0;
    this.updateDots();
    this.updateNavButtons();
  }

  updateVisibleSlides() {
    this.visibleSlides = this.slides.filter(
      (slide) => !slide.classList.contains('hidden-by-filter') && !slide.classList.contains('hidden-by-pagination')
    );
  }

  setupSlider() {
    const prevBtn = this.section.querySelector('.slider-button--prev');
    const nextBtn = this.section.querySelector('.slider-button--next');

    if (prevBtn) {
      prevBtn.addEventListener('click', () => this.prevSlide());
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => this.nextSlide());
    }

    // Touch/swipe support
    let startX = 0;
    let currentX = 0;
    let isDragging = false;

    this.slider.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      isDragging = true;
    });

    this.slider.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      currentX = e.touches[0].clientX;
    });

    this.slider.addEventListener('touchend', () => {
      if (!isDragging) return;

      const diff = startX - currentX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          this.nextSlide();
        } else {
          this.prevSlide();
        }
      }

      isDragging = false;
    });

    // Mouse drag support for desktop
    this.slider.addEventListener('mousedown', (e) => {
      startX = e.clientX;
      isDragging = true;
      this.slider.style.cursor = 'grabbing';
    });

    this.slider.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      currentX = e.clientX;
    });

    this.slider.addEventListener('mouseup', () => {
      if (!isDragging) return;

      const diff = startX - currentX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          this.nextSlide();
        } else {
          this.prevSlide();
        }
      }

      isDragging = false;
      this.slider.style.cursor = 'grab';
    });

    this.slider.addEventListener('mouseleave', () => {
      if (isDragging) {
        isDragging = false;
        this.slider.style.cursor = 'grab';
      }
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.prevSlide();
      } else if (e.key === 'ArrowRight') {
        this.nextSlide();
      }
    });

    this.updateNavButtons();
  }

  prevSlide() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateSliderPosition();
      this.updateDots();
      this.updateNavButtons();
      this.resetAutoplay();
    }
  }

  nextSlide() {
    const maxIndex = Math.max(0, this.visibleSlides.length - this.getSlidesPerView());
    if (this.currentIndex < maxIndex) {
      this.currentIndex++;
      this.updateSliderPosition();
      this.updateDots();
      this.updateNavButtons();
      this.resetAutoplay();
    }
  }

  updateSliderPosition() {
    if (!this.slider) return;

    const visibleSlide = this.visibleSlides[this.currentIndex];
    if (!visibleSlide) return;

    // Smooth scroll within slider container only (no page jump)
    const slideWidth = visibleSlide.offsetWidth;
    const scrollPosition = this.currentIndex * slideWidth;

    this.slider.scrollTo({
      left: scrollPosition,
      behavior: 'smooth',
    });
  }

  getSlidesPerView() {
    const width = window.innerWidth;
    if (width < 750) return 1;
    if (width < 990) return 2;
    return this.slidesToShow;
  }

  updateNavButtons() {
    const prevBtn = this.section.querySelector('.slider-button--prev');
    const nextBtn = this.section.querySelector('.slider-button--next');

    if (!prevBtn || !nextBtn) return;

    const maxIndex = Math.max(0, this.visibleSlides.length - this.getSlidesPerView());

    prevBtn.disabled = this.currentIndex === 0;
    nextBtn.disabled = this.currentIndex >= maxIndex;
  }

  setupDots() {
    if (!this.dotsContainer) return;

    this.updateDots();
  }

  updateDots() {
    if (!this.dotsContainer) return;

    const slidesPerView = this.getSlidesPerView();
    const totalDots = Math.ceil(this.visibleSlides.length / slidesPerView);

    this.dotsContainer.innerHTML = '';

    for (let i = 0; i < totalDots; i++) {
      const dot = document.createElement('button');
      dot.classList.add('slider-dot');
      if (i === Math.floor(this.currentIndex / slidesPerView)) {
        dot.classList.add('active');
      }

      dot.addEventListener('click', () => {
        this.currentIndex = i * slidesPerView;
        this.updateSliderPosition();
        this.updateDots();
        this.updateNavButtons();
        this.resetAutoplay();
      });

      this.dotsContainer.appendChild(dot);
    }
  }

  setupLoadMore() {
    if (!this.loadMoreBtn) return;

    // Hide reviews beyond initial count
    this.slides.forEach((slide, index) => {
      if (index >= this.initialReviews) {
        slide.classList.add('hidden-by-pagination');
      }
    });

    this.updateVisibleSlides();

    this.loadMoreBtn.addEventListener('click', () => {
      const hiddenSlides = this.slides.filter((slide) => slide.classList.contains('hidden-by-pagination'));
      const nextBatch = hiddenSlides.slice(0, this.initialReviews);

      nextBatch.forEach((slide) => {
        slide.classList.remove('hidden-by-pagination');
        slide.style.animation = 'fadeInUp 0.6s ease';
      });

      this.updateVisibleSlides();
      this.updateDots();
      this.updateNavButtons();

      // Hide button if no more reviews
      const remainingHidden = this.slides.filter((slide) => slide.classList.contains('hidden-by-pagination'));

      if (remainingHidden.length === 0) {
        this.loadMoreBtn.style.display = 'none';
      }
    });
  }

  startAutoplay() {
    this.autoplayInterval = setInterval(() => {
      const maxIndex = Math.max(0, this.visibleSlides.length - this.getSlidesPerView());

      if (this.currentIndex < maxIndex) {
        this.nextSlide();
      } else {
        this.currentIndex = 0;
        this.updateSliderPosition();
        this.updateDots();
        this.updateNavButtons();
      }
    }, this.autoplaySpeed * 1000);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  resetAutoplay() {
    if (!this.autoplay) return;

    this.stopAutoplay();
    this.startAutoplay();
  }

  setupAutoplayPause() {
    // Pause autoplay on hover
    this.section.addEventListener('mouseenter', () => {
      this.stopAutoplay();
    });

    this.section.addEventListener('mouseleave', () => {
      if (this.autoplay) {
        this.startAutoplay();
      }
    });

    // Pause autoplay when tab is not visible
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else if (this.autoplay) {
        this.startAutoplay();
      }
    });
  }

  handleResize() {
    this.updateSliderPosition();
    this.updateDots();
    this.updateNavButtons();
  }
}

// Initialize all review sections on page load
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('.belle-reviews-advanced');
  sections.forEach((section) => {
    new BelleReviewsAdvanced(section);
  });
});

// Reinitialize on Shopify theme editor changes
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.belle-reviews-advanced');
    if (section) {
      new BelleReviewsAdvanced(section);
    }
  });
}
